import { LoadingButton } from "@mui/lab"
import {
  Button,
  CardActions,
  CardContent,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
  Dialog,
} from "@mui/material"
import {
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { DialogTransition } from "../../services/utilities/utility"
import DialogTopBar from "../global/dialogTopBar"
import { useNavigate } from "react-router-dom"
import { MainContext } from "../../controllers/main"
import { NotificationsContext } from "../../controllers/notifications"
import NotificationTemplate from "../../models/notificationTemplate"
import {
  NotificationTemplateFormat,
  NotificationTemplateType,
} from "../../services/config/enum"

const DuplicateTemplateDialog = ({
  dialogOpen,
  setDialogOpen,
  handleDialogChange,
  templateToDuplicate,
}: {
  dialogOpen: boolean
  setDialogOpen: Dispatch<SetStateAction<boolean>>
  handleDialogChange: MouseEventHandler<HTMLButtonElement>
  templateToDuplicate: NotificationTemplate
}) => {
  const { setAnimation } = useContext(MainContext)
  const { setLoading, createTemplate, createDuplicatedTemplateDocument } =
    useContext(NotificationsContext)
  const navigate = useNavigate()

  // prevent tab close
  const handleTabClose = useCallback((event: any) => {
    event.preventDefault()
    console.log("beforeunload event triggered")
    return (event.returnValue = "Are you sure you want to exit?")
  }, [])

  useEffect(() => {
    if (dialogOpen) {
      window.addEventListener("beforeunload", handleTabClose)
    } else {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [dialogOpen])

  // configuration
  const [handle, setHandle] = useState<string>(templateToDuplicate.handle)

  // current slide and scrolling when changes
  const [currentSlide, setCurrentSlide] = useState<number>(0)

  useEffect(() => {
    setTimeout(() => {
      let slide = document.getElementById(currentSlide.toString())
      slide?.scrollIntoView({ behavior: "smooth" })
    }, 10)
  }, [currentSlide])

  // slides
  const slides = [
    {
      label: "Configuration",
      component: (
        <Stack spacing={2}>
          <TextField
            label="Handle"
            fullWidth
            size="small"
            value={handle}
            onChange={(e) => {
              setHandle(e.target.value)
            }}
            onBlur={() => {
              if (handle) {
                setHandle((current) => current.trim())
              }
            }}
          />
        </Stack>
      ),
    },
  ]

  // reset dialog on close
  useEffect(() => {
    if (!dialogOpen) {
      setTimeout(() => {
        setHandle(templateToDuplicate.handle)
        setCurrentSlide(0)
      }, 100)
    }
  }, [dialogOpen])

  // discard dialog
  const [discardDialogOpen, setDiscardDialogOpen] = useState<boolean>(false)

  const handleDiscardDialogCancel = () => {
    setDiscardDialogOpen(false)
  }

  const handleDiscardDialogClose = (e: any) => {
    setDiscardDialogOpen(false)
    handleDialogChange(e)
  }

  // should show discard dialog
  const [showDiscardDialog, setShowDiscardDialog] = useState<boolean>(false)

  useEffect(() => {
    if (handle) {
      setShowDiscardDialog(true)
    } else {
      setShowDiscardDialog(false)
    }
  }, [handle])

  // create template
  const createNewTemplate = async () => {
    setLoading(true)

    const input: {
      handle: string
      format: NotificationTemplateFormat
      type: NotificationTemplateType
      defaultDataJson?: string
      requiredEnricherIds?: string[]
    } = {
      handle,
      format: templateToDuplicate.format,
      type: templateToDuplicate.type,
      ...(templateToDuplicate.defaultDataJson
        ? { defaultDataJson: templateToDuplicate.defaultDataJson }
        : {}),
      ...(templateToDuplicate.requiredEnricherIds
        ? { requiredEnricherIds: templateToDuplicate.requiredEnricherIds }
        : {}),
    }

    const result = await createTemplate(input)

    if (typeof result !== "boolean") {
      const newTemplateId = result

      const documentResult = await createDuplicatedTemplateDocument(
        templateToDuplicate,
        newTemplateId
      )

      if (typeof documentResult !== "boolean") {
        setDialogOpen(false)
        setAnimation(false)
        setTimeout(() => {
          setLoading(false)
          navigate("/templates")
        }, 250)
      } else {
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }

  return (
    <Dialog
      fullScreen
      open={dialogOpen}
      onClose={
        showDiscardDialog
          ? () => {
              setDiscardDialogOpen(true)
            }
          : handleDialogChange
      }
      TransitionComponent={DialogTransition}
    >
      <DialogTopBar
        handleDialogChange={
          showDiscardDialog
            ? () => {
                setDiscardDialogOpen(true)
              }
            : handleDialogChange
        }
        topBarText="Duplicate Template"
      />
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          variant="outlined"
          style={{
            width: "70%",
            minWidth: 345,
            maxWidth: 850,
            height: 500,
            position: "relative",
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            style={{ paddingTop: 16 }}
          >
            Duplicate Template
          </Typography>
          <div
            style={{
              minWidth: 345,
              maxWidth: 850,
              height: "auto",
            }}
          >
            {slides.map((slide, index) => (
              <div
                key={index}
                id={index.toString()}
                style={{ minWidth: "100%", scrollSnapAlign: "center" }}
              >
                <CardContent>{slide.component}</CardContent>
              </div>
            ))}
          </div>
          <CardActions
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              padding: 16,
              width: "100%",
            }}
          >
            <div style={{ width: "50%" }}>
              <Button
                variant="outlined"
                onMouseDown={(e: any) => {
                  if (!slides[currentSlide - 1]) {
                    if (showDiscardDialog) {
                      setDiscardDialogOpen(true)
                    } else {
                      handleDialogChange(e)
                    }
                  } else {
                    setCurrentSlide(currentSlide - 1)
                  }
                }}
              >
                Close
              </Button>
            </div>
            <div
              style={{
                width: "50%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <LoadingButton
                variant="contained"
                disabled={currentSlide === 0 && !handle}
                onMouseDown={
                  !slides[currentSlide + 1]
                    ? createNewTemplate
                    : () => {
                        setCurrentSlide(currentSlide + 1)
                      }
                }
              >
                Duplicate
              </LoadingButton>
            </div>
          </CardActions>
        </Paper>
      </div>
      <Dialog
        open={discardDialogOpen}
        onClose={handleDiscardDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to close this window?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            All entered data will be lost
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardDialogCancel}>Cancel</Button>
          <Button onClick={handleDiscardDialogClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default DuplicateTemplateDialog
